import React, { FunctionComponent, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Badge, Button, Checkbox,  Modal, Radio } from "@scuf/common";
import {  ACTION_UPLOAD_TYPE, CHECKLIST_UPLOAD_TYPE, FILE_UPLOAD_WARNING, ACTION_ALREADY_COMPLETED_UPLOAD_TEXT, RouteConstants, ERROR_MSG_FOR_USER_RESPONSE, PROPOSED_ACTION_TRACKING_DISPLAY_CATEGORIES } from "../../../../constants/constants";
import { getReasons, getChecklist, reviewerChecklistSave, getTrainingOptions, saveProposedActionTracking } from "../../../../services/Opportunities";
import { getReviewerUploadedFiles } from "./uploadedFiles";
import { checkSpecialCharacters } from "src/utils/validationUtils";
import { FileUpload } from "../../../UploadFileModal/FileUpload";
import { getCurrentRole } from 'src/slices/onBoardingSlice';
import { Progress } from '@scuf/common';
import { LocalizationConfig } from '@scuf/localization';
import { TextBoxOperation } from './TextBoxAction/TextboxOperation'
import RadioActionStatus from "./RadioActionStatus/RadioActionStatus";
import { AttachmentDownload } from "../AttachmentDownload";
import { Snackbar } from '../../../Snackbar/Snackbar';
import { toast, ToastContent, Zoom } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import "./ReviewerCheckList.scss"
import "../../AcceptPopup.scss";
import "../../NewCardDesign/CommonStyle.scss";
import FontIcon from "src/components/FontIcon/FontIcon";
import { getMessage } from "src/message/utils";
import DOMPurify from 'dompurify';


interface IAcceptPopup {
  onClose: Function;
  insightId: any;
  kpiId: number;
  title: string;
  textAreaTitle: string;
  opportunityId: any;
  sendUserResponse: Function;
  activityDetails: any;
  closeAction?: Function;
  role_name?: string;
  anomalyId?: string;
  orguserId?: string;
  errorMessage: string;
  insightSummaryDisplayCategory?: string;
  modelId?: number;
}

const TRAINING_REQUIRED_ACTION_TYPE = 'acceptanddropdown';
const ACTION_OWNER_REQUIRED_ACTION_TYPE = 'acceptwithproposedactionowner';

type ToastType =
  | 'reviewer-checklist-save' | 'reviewer-checklist-submit';
export const ReviewerCheckList: FunctionComponent<IAcceptPopup> = (props) => {
  const [btnText, setBtnText] = useState("Submit");
  const [remark, setRemark] = useState("");
  const [acceptReason, setAcceptReason] = useState([] as any);
  const [reasonLoading, setReasonLoading] = useState(true);
  const [reasonError, setReasonError] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const currentRole = useSelector(getCurrentRole);
  const [reviewPercentage, setReviewPercentage] = useState(0);
  const [actionStatus, setActionStatus] = useState("");
  const [remarkActionRequired, setRemarkActionRequired] = useState("");
  const [remarkNoActionRequired, setRemarkNoActionRequired] = useState("");
  const [reasonStringActionRequired, setReasonStringActionRequired] = useState("");
  const [reasonStringNoActionRequired, setReasonStringNoActionRequired] = useState("");
  const [reviewerCheckListValue, setReviewerCheckListValue] = useState([] as any);
  const navigate = useNavigate();
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [selectedAction, setselectedAction] = useState("")
  const warningText = getMessage('app.notes.footer.warning');

  // Per-reason action-owner proposal data reported up from RadioActionStatus (only used when isTrackingFlow is true)
  const [trackingData, setTrackingData] = useState<any[]>([]);
  const [trackingComplete, setTrackingComplete] = useState(true);
  const [trainingOptions, setTrainingOptions] = useState([] as any);

  const isTrackingFlow = PROPOSED_ACTION_TRACKING_DISPLAY_CATEGORIES.includes(props.insightSummaryDisplayCategory || '');
  const isUI5 = props.insightSummaryDisplayCategory === 'UI 5';
  const [uploadEvidence, setUploadEvidence] = useState({
    opportunityId: props.opportunityId,
    hasFile: false,
    error: false
  });
  const hasUploadedEvidence = uploadEvidence.opportunityId === props.opportunityId && uploadEvidence.hasFile;

  useEffect(() => {
    if (!isUI5) {
      return;
    }
    let active = true;
    setUploadEvidence({ opportunityId: props.opportunityId, hasFile: false, error: false });
    getReviewerUploadedFiles(props.opportunityId)
      .then(response => {
        const hasFile = response.activity.some(activity =>
          (activity.type === ACTION_UPLOAD_TYPE || activity.type === CHECKLIST_UPLOAD_TYPE) &&
          typeof activity.file_details?.file_name === 'string' &&
          activity.file_details.file_name.trim().length > 0
        );
        if (active) {
          setUploadEvidence(previous => ({
            opportunityId: props.opportunityId,
            hasFile: previous.hasFile || hasFile,
            error: false
          }));
        }
      })
      .catch(() => {
        if (active) {
          setUploadEvidence(previous => ({
            opportunityId: props.opportunityId,
            hasFile: previous.opportunityId === props.opportunityId && previous.hasFile,
            error: true
          }));
        }
      });
    return () => { active = false; };
  }, [isUI5, props.opportunityId]);

  const handleUploadSuccess = () => {
    setUploadEvidence({ opportunityId: props.opportunityId, hasFile: true, error: false });
  };

  useEffect(() => {
    setReasonLoading(true);
    setReasonError(false);
    Promise.all([
      getReasons(props.opportunityId, "accept"),
      getReasons(props.opportunityId, TRAINING_REQUIRED_ACTION_TYPE),
      getReasons(props.opportunityId, ACTION_OWNER_REQUIRED_ACTION_TYPE)
    ])
      .then(([acceptReasons, trainingReasons, actionOwnerReasons]) => {
        setReasonLoading(false);
        setAcceptReason([...acceptReasons, ...trainingReasons, ...actionOwnerReasons]);
      })
      .catch(() => {
        setReasonLoading(false);
        setReasonError(true);
      });
  }, []);

  useEffect(() => {
    setReasonLoading(true);
    setReasonError(false);
    getChecklist(props.opportunityId, currentRole)
      .then((response) => {
        setReasonLoading(false);
        setReviewerCheckListValue(response);
      })
      .catch(() => {
        setReasonLoading(false);
        setReasonError(true);
      });
  }, []);
  useEffect(() => {
    if (props.errorMessage.length > 0) {
      setBtnText("Retry");
    }
  }, [props.errorMessage]);

  useEffect(() => {
    if (isTrackingFlow) {
      getTrainingOptions()
        .then((response: any) => {
          const options = (response || []).map((item: any) => ({
            value: item.training_name,
            text: item.training_name
          }));
          setTrainingOptions(options);
        })
        .catch(() => {
          setTrainingOptions([]);
        });
    }
  }, []);

  const handleCheckboxData = (checked: boolean, labelId: any, operation: any, is_mandatory: any, actionResponse: any) => {

    let reviewerCheckListResponseData = [...reviewerCheckListValue];
    if (reviewerCheckListResponseData.length > 0) {
      let foundIndex = reviewerCheckListResponseData.findIndex(element => element.label_id === labelId)
      if (operation != "radio" && is_mandatory && (actionResponse === "null" || actionResponse === "")) {
        reviewerCheckListResponseData[foundIndex]["is_checked"] = false;

      } else if (is_mandatory && operation === "radio") {
        reviewerCheckListResponseData[foundIndex]["is_checked"] = !checked;
      }
      else {
        reviewerCheckListResponseData[foundIndex]["is_checked"] = !checked;
      }
      setReviewerCheckListValue(reviewerCheckListResponseData);
    }
  }

  const handleResponseData = (labelId: any, actionResponse: any, operation: any) => {
    let reviewerCheckListResponseData = [...reviewerCheckListValue];
    if (reviewerCheckListResponseData.length > 0) {
      let foundIndex = reviewerCheckListResponseData.findIndex(element => element.label_id === labelId)
      if (operation === "radio") {
        reviewerCheckListResponseData[foundIndex]["action_response"] = actionResponse.user_response;
        reviewerCheckListResponseData[foundIndex]["remarks"] = actionResponse.remarks;

      } else {
        reviewerCheckListResponseData[foundIndex]["action_response"] = actionResponse;
      }
      setReviewerCheckListValue(reviewerCheckListResponseData);
    }
  }




  const displayContent = (description: any, actiondata: any, operation: any, actionResponse = "", labelId: number, isMandatory: any, action: any, label_name: any, remarks: any, action_for: any) => {
    switch (description.type) {
      case "text":
        let resultText: any = <div>{description.content}
          {
            operation ? displayOperation(operation, actiondata, actionResponse, labelId, isMandatory, action, label_name, remarks, action_for) : null}
        </div>
        return resultText;
      case "list":
        let resultList = <ul>{description.content.map((item: any) => { return (<li>        {item}</li>) })}  </ul>

        return <>{resultList} {
          operation ? displayOperation(operation, actiondata, actionResponse, labelId, isMandatory, action, label_name, remarks, action_for) : null}</>;
      default:
        return null;
    }
  }


  const displayOperation = (operation: any, actiondata: any, actionResponse: any, labelId: number, isMandatory: boolean, action: any, label_name: any, remarks: any, action_for:any) => {
    switch (operation) {
      case "edit":
        let textboxoperation: any = <TextBoxOperation
          actionResponse={actionResponse}
          handleText={handleText}
          labelId={labelId} />
        return textboxoperation;
      case "radio":
        let resultList = <RadioActionStatus
          actionResponse={actionResponse}
          action={action}
          reviewPercentage={reviewPercentage}
          handleRadio={handleRadio}
          labelId={labelId}
          label_name={label_name}
          remarks={remarks}
          action_for={action_for}
          orguserId={props.orguserId}
          anomalyId={props.anomalyId}
          kpiId={props.kpiId}
          operation="radio"
          opportunityId={props.opportunityId}
          handleCheckboxData={handleCheckboxData}
          isMandatory={isMandatory}
          isTrackingFlow={isTrackingFlow}
          trainingOptions={trainingOptions}
          trainingRequiredActionType={TRAINING_REQUIRED_ACTION_TYPE}
          actionOwnerRequiredActionType={ACTION_OWNER_REQUIRED_ACTION_TYPE}
          onTrackingDataChange={handleTrackingDataChange}
          onUploadSuccess={isUI5 ? handleUploadSuccess : undefined}
          isEvidenceRequired={isUI5 && !hasUploadedEvidence}
          isEvidenceError={isUI5 && uploadEvidence.error}
        />
        return resultList;
      case "upload":
        let resultLUpload = <div >
          <div className="accept-popup__uploadfiles" >
            Upload Files
            {isUI5 && !hasUploadedEvidence && (
              <span
                className="upload-required-indicator"
                role="img"
                aria-label={uploadEvidence.error
                  ? "Supporting file required; saved files could not be verified"
                  : "Supporting file required"}
              >
                <Badge empty={true} color="red" />
              </span>
            )}
          </div>
          {<div className="accept-popup__uploadfilesnote">{ACTION_ALREADY_COMPLETED_UPLOAD_TEXT}</div>}
          <div className="upload-wrapper_reasons">
          <div className='email-error mt-3' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(warningText,{
                ALLOWED_ATTR:['href', 'target']
            }) }}></div>
            <FileUpload
              className="document-upload" // @ts-ignore
              anomaly_id={props.opportunityObjectName}
              opportunityId={props.opportunityId}
              type={CHECKLIST_UPLOAD_TYPE}
              onUploadSuccess={isUI5 ? handleUploadSuccess : undefined}
              updateUploadedStatus={(uploaded: boolean) => setIsUploaded(uploaded)}
            />
          </div>
          {isUploaded && <div className="accept-popup__remarkerr">{FILE_UPLOAD_WARNING}</div>}
          <>{actionResponse?.map((file: any, index: number) => {
            return (                
            <div key={`item-${index}`} className="file-value-wrapper-content">
              <FontIcon className='response-list' iconName="fa-paperclip">{file.file_name}</FontIcon>
              <AttachmentDownload opportunityId={props.opportunityId} fileName={file.file_name} storedFileName={file.stored_file_name} />
            </div>
            )
          })
          }
          </>
        </div>
        return resultLUpload;
        case "radio_test":
          let radioOperation: any = 
          action.map((data:any) => (
            <Radio
              label={data}
              checked={selectedAction === data}
              onChange={() => setselectedAction(data)}
            />
          ))          
        return radioOperation;
      default:
        return null;
    }
  }


  const handleText = (data: any, labelId: number) => {
    handleResponseData(labelId, data, "edit")
  }

  const handleRadio = (data: any, labelId: number) => {
    handleResponseData(labelId, data, "radio")
    if (data.user_response === "Action Required") {
      setActionStatus("Action Required")
      handleResponseData(labelId, data, "radio")
      setRemarkActionRequired(data.remarks);
      setReasonStringActionRequired(data.reason)
    } else if (data.user_response === "No Action Required"){
      setActionStatus("No Action Required")
      handleResponseData(labelId, data, "radio")
      setRemarkNoActionRequired(data.remarks);
      setReasonStringNoActionRequired(data.reason)
    }
    else {
      // setActionStatus(data.user_response)
      handleResponseData(labelId, data, "radio")
      // setReasonStringNoActionRequired(data.reason)
    }
  }

  const handleTrackingDataChange = (data: any[], isComplete: boolean) => {
    setTrackingData(data);
    setTrackingComplete(isComplete);
  };

  useEffect(() => {
    if (!reviewerCheckListValue.length) {
      setReviewPercentage(0);
      return;
    }

    let completedCount = reviewerCheckListValue.filter((ele: any) => ele.is_checked === true).length;
    const trackingIncomplete =
      isTrackingFlow &&
      actionStatus === "Action Required" &&
      trackingData.length > 0 &&
      !trackingComplete;

    if (trackingIncomplete) {
      const mandatoryRadioChecked = reviewerCheckListValue.some(
        (item: any) => item.is_mandatory && item.operation === "radio" && item.is_checked
      );
      if (mandatoryRadioChecked) {
        completedCount = Math.max(0, completedCount - 1);
      }
    }

    const resultPer: number = (completedCount / reviewerCheckListValue.length) * 100;
    setReviewPercentage(Math.round(resultPer));
  }, [
    reviewerCheckListValue,
    isTrackingFlow,
    actionStatus,
    trackingData,
    trackingComplete,
  ]);

  const handleSaveClick = (submitType: any) => {
    // SECURITY_NOTE: Only a persisted attachment satisfies UI 5 submission validation.
    if (submitType === "submit" && isUI5 && !hasUploadedEvidence) {
      return;
    }
    let reviewerCheckListValueLocal = [...reviewerCheckListValue]
    let dataResponse: any = {
      current_role: currentRole,
      user_response: "draft",
      kpi_id: props.kpiId,
    };

    if (actionStatus === "Action Required" && reviewPercentage === 100 && submitType === "submit") {
      setSubmitDisabled(true)
      dataResponse = {
        current_role: currentRole,
        user_response: "accept",
        kpi_id: 1,
        remarks: remarkActionRequired,
        reason: reasonStringActionRequired,
        orguser_id: props.orguserId,
        anomaly_id: props.anomalyId,
      };
    } else if (actionStatus === "No Action Required" && reviewPercentage === 100 && submitType === "submit") {
      setSubmitDisabled(true)
      dataResponse = {
        current_role: currentRole,
        user_response: "reject",
        kpi_id: 1,
        remarks: remarkNoActionRequired,
        reason: reasonStringNoActionRequired,
        orguser_id: props.orguserId,
        anomaly_id: props.anomalyId,
      };
    } else {
      dataResponse = {
        current_role: currentRole,
        user_response: "draft",
        kpi_id: props.kpiId,
        orguser_id: props.orguserId,
        anomaly_id: props.anomalyId,
      };
    }


    let modifiedreviewerChecklistValue = reviewerCheckListValueLocal.map((data: any) => { const { label_id, is_checked, action_response, remarks } = data; return { label_id, is_checked, action_response, remarks } })
    dataResponse["checklist_response"] = modifiedreviewerChecklistValue;

    const isSubmittingTrackingFlow =
      isTrackingFlow &&
      trackingData.length > 0 &&
      submitType === "submit" &&
      actionStatus === "Action Required" &&
      reviewPercentage === 100;

    const trackingInfo = isSubmittingTrackingFlow
      ? trackingData.flatMap((reasonItem: any) =>
        (reasonItem.data || []).map((row: any) => {
          const { isValid, createdOn, ...rest } = row;
          return {
            ...rest,
            reason_id: reasonItem.reason_id,
            reason: reasonItem.reason,
          };
        })
      )
      : [];

    if (isSubmittingTrackingFlow) {
      dataResponse.tracking_info = trackingInfo;
    }

    const onSuccess = () => {
      if (submitType === "submit") {
        props.onClose();
        navigate(RouteConstants.ANOMALY_ABSOLUTE_PATH);
        showActionToast(
          <Snackbar toastMessage="Reviewed Successfully" />,
          'reviewer-checklist-submit'
        );
      }
    };

    const onFailure = (err: any) => {
      const errorData = err.response.data;
      const message = errorData?.message || ERROR_MSG_FOR_USER_RESPONSE;
      setSubmitDisabled(false)
      showActionToast(
        <Snackbar toastMessage={message} />,
        'reviewer-checklist-submit'
      )
    };

    if (isSubmittingTrackingFlow) {
      if (!trackingComplete) {
        setSubmitDisabled(false);
        return;
      }
      let finalData = {
        "user_response": dataResponse
      }
      reviewerChecklistSave(props.anomalyId, finalData)
        .then(() => {
          return saveProposedActionTracking(props.anomalyId, trackingInfo);
        })
        .then(onSuccess)
        .catch(onFailure);
    } else {
      let finalData = {
        "user_response": dataResponse
      }
      reviewerChecklistSave(props.anomalyId, finalData).then(onSuccess).catch(onFailure);
    }
  }
  const showActionToast = (toastContent: ToastContent, type: ToastType) => {
    if (type === 'reviewer-checklist-submit') {
      toast(toastContent, {
        position: 'bottom-right',
        className: 'fi-toast--snackbar',
        bodyClassName: 'fi-toast__body--snackbar',
        transition: Zoom,
        closeOnClick: true,
      });
    }
    else if (type === 'reviewer-checklist-save') {
      toast(toastContent, {
        position: 'bottom-right',
        className: 'fi-toast--snackbar',
        bodyClassName: 'fi-toast__body--snackbar',
        transition: Zoom,
        closeOnClick: true,
      });
    }
  };


  return (
    <>
      <Modal.Content>
        <div>
          <div className="sticky">
            <div className="revewer-checklist-progress">
              <LocalizationConfig >
                <Progress percent={reviewPercentage} circular={true} size='medium' />
              </LocalizationConfig>
              <div className="revewer-checklist-progress-complete">COMPLETED</div>
            </div>
          </div>

          <div>
            {reviewerCheckListValue.map((data: any, index: number) => {
              return (
                <div key={`check-item-${index}`} className={'check-item-container'}>
                  {data.operation==="radio"?<div className="mandatory-radio-operation">{ data.label_name}</div>:<Checkbox
                    className="checkbox-fi"
                    label={
                      data.label_name}
                    checked={data.is_checked}
                    disabled={data.is_mandatory}
                    onChange={() => {
                      handleCheckboxData(data.is_checked, data.label_id, data.operation, data.is_mandatory, data.action_response);
                    }}
                  />}
                  <div>
                    {
                      displayContent(data.description, data.action_response, data.operation, data.action_response, data.label_id, data.is_mandatory, data.action, data.label_name, data.remarks, data.action_for)}
                  </div>

                  <div className="reviewer-important">
                    {data.important_text}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="button-footer-container">

          <Button
            className="button--secondary button-space button-submit"
            data-testid="submit"
            size="small"
            type="secondary"
            disabled={reviewPercentage !== 100 || submitDisabled || (isUI5 && !hasUploadedEvidence) || (isTrackingFlow && actionStatus === "Action Required" && trackingData.length > 0 && !trackingComplete)}
            onClick={() => {
              handleSaveClick("submit")
            }
            }
            content="Submit"
          />
          <Button
            className="button--secondary button-space button-save"
            data-testid="save"
            size="small"
            type="secondary"
            onClick={() => {
              handleSaveClick("save")
            }
            }
            content="Save"
          />
        </div>
     </Modal.Content>
    </>
  );
};